import React, { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Button } from '@/components/ui/button';
import { Camera, RefreshCw, Check, X } from 'lucide-react';

/**
 * WebcamCapture
 * Wrapper sobre react-webcam que maneja permisos, captura, preview y reset.
 *
 * Props:
 *  - onCapture(blob): callback cuando el usuario acepta la foto. Devuelve un Blob JPEG.
 *  - onCancel(): callback si el usuario descarta la foto.
 *  - label: texto opcional que se muestra sobre el video (ej: "Entrada" o "Salida")
 *  - className: clases extra para el contenedor
 */
const WebcamCapture = ({ onCapture, onCancel, label, className = '' }) => {
  const webcamRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' = frontal, 'environment' = trasera

  // Convierte el dataURL que devuelve getScreenshot a un Blob
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      setError('No se pudo obtener la imagen. Intenta de nuevo.');
      return;
    }
    setImgSrc(imageSrc);
  }, []);

  const handleAccept = useCallback(() => {
    if (!imgSrc) return;
    const blob = dataURLtoBlob(imgSrc);
    onCapture?.(blob, imgSrc);
  }, [imgSrc, onCapture]);

  const handleRetake = useCallback(() => {
    setImgSrc(null);
    setError(null);
  }, []);

  const handleSwitchCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  }, []);

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      {label && (
        <div className="text-sm font-medium text-gray-700 self-start">
          {label}
        </div>
      )}

      <div className="relative w-full max-w-md aspect-[4/3] bg-gray-900 rounded-lg overflow-hidden shadow-md">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt="Captura"
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                facingMode,
                width: { ideal: 1280 },
                height: { ideal: 720 },
              }}
              onUserMediaError={(err) => {
                console.error('Webcam error:', err);
                setError('No se pudo acceder a la cámara. Verifica los permisos del navegador.');
              }}
              className="w-full h-full object-cover"
            />
            {/* Marco guía */}
            <div className="pointer-events-none absolute inset-6 border-2 border-white/60 rounded-md" />
          </>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 p-4">
            <p className="text-white text-sm text-center">{error}</p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 justify-center">
        {imgSrc ? (
          <>
            <Button type="button" variant="outline" onClick={handleRetake} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Repetir
            </Button>
            <Button type="button" onClick={handleAccept} className="gap-2 bg-green-600 hover:bg-green-700">
              <Check className="h-4 w-4" />
              Usar esta foto
            </Button>
            {onCancel && (
              <Button type="button" variant="ghost" onClick={onCancel} className="gap-2">
                <X className="h-4 w-4" />
                Cancelar
              </Button>
            )}
          </>
        ) : (
          <>
            <Button type="button" onClick={handleCapture} className="gap-2" disabled={!!error}>
              <Camera className="h-4 w-4" />
              Tomar foto
            </Button>
            <Button type="button" variant="outline" onClick={handleSwitchCamera} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Cambiar cámara
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default WebcamCapture;
